import React from "react";
import {
  Box,
  Stack,
  Text,
  Heading,
  Divider,
  Column,
  Columns,
  RadioGroup,
  Radio,
  Checkbox,
  Button,
  TextLink,
  TextField,
} from "@origin-digital/ods-core";
import { Field, useField } from "formik";
import ResponsiveColumn from "./ResponsiveColumn";
import { useFormValidation } from "./useFormValidation";
import { useAccountTypeHandler } from "./useAccountTypeHandler";
import ReadOnlyTextField from "../FormComponents/ReadOnlyTextField";
import { getConnectionTypeLabels } from "./helper";
import { GeneralTextFieldWithAutoCompleteOn } from "../FormComponents/GeneralTextFieldWithAutoCompleteOn";
import { DateOfBirthFormikField } from "src/components/Fields/DateOfBirth";

interface EICFormPageTwoProps {
  onBack: () => void;
  onContinue: () => void;
}

const PrimaryContactBlock = ({
  isBusiness = false,
}: {
  isBusiness?: boolean;
}) => {
  const [phoneField, phoneMeta, phoneHelpers] = useField("phone");
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
    phoneHelpers.setValue(value);
  };

  return (
    <ResponsiveColumn collapseBelow="xl">
      {/* Fields */}
      <Stack space={["xlarge", "xlarge"]}>
        <Field
          component={GeneralTextFieldWithAutoCompleteOn}
          name="firstName"
          label="First name"
        />
        <Field
          component={GeneralTextFieldWithAutoCompleteOn}
          name="lastName"
          label="Last name"
        />
        {!isBusiness && (
          <ResponsiveColumn>
            <DateOfBirthFormikField
              name={"dateOfBirth"}
              label={"Date of birth"}
              rules={{
                required: true,
                noInvalidDate: true,
                noFuture: true,
                noKids: true,
              }}
            />
          </ResponsiveColumn>
        )}
        <ResponsiveColumn>
          <TextField
            id="phone"
            variant="outlined"
            label="Phone (Mobile or landline)"
            value={phoneField.value}
            onChange={handlePhoneChange}
            onBlur={phoneField.onBlur}
            error={Boolean(phoneMeta.touched && phoneMeta.error)}
            helperText={
              <Columns>
                <Column>{phoneMeta.touched && phoneMeta.error}</Column>
                <Column width="content">{phoneField.value?.length}/10</Column>
              </Columns>
            }
            domProps={
              {
                autoComplete: "on",
              } as React.HTMLAttributes<HTMLInputElement>
            }
          />
        </ResponsiveColumn>
        <Field
          component={GeneralTextFieldWithAutoCompleteOn}
          name="email"
          label="Email"
          type="email"
        />
      </Stack>
      {/* End Fields */}
    </ResponsiveColumn>
  );
};

const EICFormPageTwo: React.FC<EICFormPageTwoProps> = ({
  onBack,
  onContinue,
}) => {
  const [addressField] = useField("address");
  const [connectionTypesField] = useField("connectionTypes");
  const [accountTypeField] = useField("accountType");
  const [addSecondaryField, , addSecondaryHelpers] = useField(
    "addSecondaryAccountHolder"
  );
  const [addSecondaryToBillsField, , addSecondaryToBillsHelpers] = useField(
    "addSecondaryToBills"
  );
  const [secondaryPhoneField, secondaryPhoneMeta, secondaryPhoneHelpers] =
    useField("secondaryPhone");
  const [abnField, abnMeta, abnHelpers] = useField("abn");

  const { handleAccountTypeChange } = useAccountTypeHandler();
  const { isValidating, handleContinueClick } = useFormValidation(onContinue);

  const handleSecondaryPhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
    secondaryPhoneHelpers.setValue(value);
  };

  const handleABNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, "").slice(0, 11);
    abnHelpers.setValue(value);
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleAccountTypeChange(e.target.value);
  };

  return (
    <Stack
      space={["xlarge", "xlarge"]}
      data-role="section"
      id="eic-form-page-two"
    >
      {/* Content */}
      <Stack>
        <Heading variant="h3">Account details</Heading>
        <Text>
          Let us know who's going to be responsible for the new account.
        </Text>
      </Stack>
      {/* Content */}

      {/* Request details */}
      <Stack space="large">
        <Heading variant="h4">Premises details</Heading>
        {/* Fields */}
        <Stack>
          {/* Premise address */}
          <ReadOnlyTextField
            label="Premise address"
            value={addressField.value}
          />
          {/* End Premise address */}

          {/* What's being connected */}
          <ReadOnlyTextField
            label="What's being connected?"
            value={getConnectionTypeLabels(connectionTypesField.value, true)}
          />

          {/* End What's being connected */}
        </Stack>
        {/* End Fields */}
      </Stack>
      {/* End Request details */}

      {/* Account holder details */}
      <Stack space={["large", "xlarge"]}>
        <Heading variant="h4">Account holder</Heading>
        <RadioGroup
          label="What type of account holder are you?"
          name="account-type"
          value={accountTypeField.value}
          onChange={handleRadioChange}
        >
          <Radio id="personal" label="Personal" value="personal" />
          <Radio id="business" label="Business" value="business" />
        </RadioGroup>
        {accountTypeField.value === "personal" && (
          <Stack space={["large", "xlarge"]}>
            <Text>
              The primary account holder is solely financially liable for the
              account, even if a secondary is also listed.
            </Text>
            <PrimaryContactBlock />
          </Stack>
        )}
        {accountTypeField.value === "business" && (
          <Stack space={["large", "xlarge"]}>
            <ResponsiveColumn collapseBelow="xl">
              {/* Fields */}
              <Stack space={["xlarge", "xlarge"]}>
                <Stack space={["xlarge", "xlarge"]}>
                  <Field
                    component={GeneralTextFieldWithAutoCompleteOn}
                    name="companyName"
                    label="Company name"
                  />
                  <ResponsiveColumn>
                    <TextField
                      id="abn"
                      variant="outlined"
                      label="ABN"
                      value={abnField.value}
                      onChange={handleABNChange}
                      onBlur={abnField.onBlur}
                      error={Boolean(abnMeta.touched && abnMeta.error)}
                      helperText={
                        <Columns>
                          <Column>{abnMeta.touched && abnMeta.error}</Column>
                          <Column width="content">
                            {abnField.value?.length}/11
                          </Column>
                        </Columns>
                      }
                      domProps={
                        {
                          autoComplete: "on",
                        } as React.HTMLAttributes<HTMLInputElement>
                      }
                    />
                  </ResponsiveColumn>
                </Stack>
              </Stack>
              {/* End Fields */}
            </ResponsiveColumn>
          </Stack>
        )}
      </Stack>
      {/* End Account holder details */}

      {accountTypeField.value === "business" && (
        <Stack space={["large", "xlarge"]}>
          <Heading variant="h4">Primary contact</Heading>
          <PrimaryContactBlock isBusiness />
        </Stack>
      )}

      {/* Secondary account holder details */}
      {accountTypeField.value === "personal" && (
        <Stack space={["large", "xlarge"]}>
          <Heading variant="h4">Secondary account holder</Heading>
          <Text>
            A secondary account holder is someone who has full access to your
            account but is not financially responsible. This means that they can
            make enquiries about the account, update postal details, accept
            offers, close the account and arrange disconnection, but they cannot
            apply a concession in their name or request an abolishment.
          </Text>
          <ResponsiveColumn collapseBelow="xl">
            {/* Fields */}
            <Stack space={["xlarge", "xlarge"]}>
              <Checkbox
                id="addSecondaryAccountHolder"
                label="Add secondary account holder"
                checked={addSecondaryField.value || false}
                onChange={(e) => addSecondaryHelpers.setValue(e.target.checked)}
              />
              {/* Secondary account holder fields show here */}
              {addSecondaryField.value && (
                <Stack space={["xlarge", "xlarge"]}>
                  <Field
                    component={GeneralTextFieldWithAutoCompleteOn}
                    name="secondaryFirstName"
                    label="First name"
                  />
                  <Field
                    component={GeneralTextFieldWithAutoCompleteOn}
                    name="secondaryLastName"
                    label="Last name"
                  />
                  <ResponsiveColumn>
                    <DateOfBirthFormikField
                      name={"secondaryDateOfBirth"}
                      label={"Date of birth"}
                      rules={{
                        required: true,
                        noInvalidDate: true,
                        noFuture: true,
                        noKids: true,
                      }}
                    />
                  </ResponsiveColumn>
                  <ResponsiveColumn>
                    <TextField
                      id="secondaryPhone"
                      variant="outlined"
                      label="Phone (Mobile or landline)"
                      value={secondaryPhoneField.value}
                      onChange={handleSecondaryPhoneChange}
                      onBlur={secondaryPhoneField.onBlur}
                      error={Boolean(
                        secondaryPhoneMeta.touched && secondaryPhoneMeta.error
                      )}
                      helperText={
                        <Columns>
                          <Column>
                            {secondaryPhoneMeta.touched &&
                              secondaryPhoneMeta.error}
                          </Column>
                          <Column width="content">
                            {secondaryPhoneField.value.length}/10
                          </Column>
                        </Columns>
                      }
                      domProps={
                        {
                          autoComplete: "on",
                        } as React.HTMLAttributes<HTMLInputElement>
                      }
                    />
                  </ResponsiveColumn>
                  <Field
                    component={GeneralTextFieldWithAutoCompleteOn}
                    name="secondaryEmail"
                    label="Email"
                    type="email"
                  />
                  <Checkbox
                    id="addSecondaryToBills"
                    label="Add secondary account holder's name to Origin bills"
                    checked={addSecondaryToBillsField.value || false}
                    onChange={(e) =>
                      addSecondaryToBillsHelpers.setValue(e.target.checked)
                    }
                  />
                </Stack>
              )}
            </Stack>
            {/* End Fields */}
          </ResponsiveColumn>
          {/* Secondary account holder disclaimer */}
          {addSecondaryField.value && (
            <Text variant="body-small" tone="neutralLight">
              You'll need to let the secondary account holder know that their
              information has been provided to us and tell them about our
              privacy and credit reporting statements which are available at{" "}
              <TextLink href="https://originenergy.com.au/privacy">
                originenergy.com.au/privacy
              </TextLink>
              .
            </Text>
          )}
        </Stack>
      )}
      {/* End Secondary account holder details */}

      <Divider />

      {/* Actions  */}
      <Box display="flex" justifyContent="space-between">
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleContinueClick} spinner={isValidating}>
          Continue
        </Button>
      </Box>
    </Stack>
  );
};

export default EICFormPageTwo;
